import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../utils/axiosInstance';
import DateTimeDisplay from '../components/DateTimeDisplay';
import ItemsNeededByDate from './ItemsNeededByDate';
import IncomeWidget from './widgets/IncomeWidget';
import IncomeExpenseWidget from './widgets/IncomeExpenseWidget';
import InOut from './widgets/InOut';
import AccountStatusCard from './widgets/AccountsStatus';
import { Plus, ShoppingCart, Users, FileText, Receipt, DollarSign } from 'lucide-react';
import Loading from './common/Loading';
import { useMessage } from '../contexts/MessageContext';

const Dashboard = () => {
  const [profileData, setProfileData] = useState(null);
  const [summary, setSummary] = useState(null);
  const { showMessage } = useMessage();
  const [isLoading, setIsLoading] = useState(true);

  const navigate = useNavigate();
  const { logout, getToken } = useAuth();

  useEffect(() => {
    const token = getToken();

    if (!token) {
      logout();
      navigate('/login');
      return;
    }

    setIsLoading(true);
    Promise.all([
      axiosInstance.get('/user/profile'),
      axiosInstance.get('/dashboard/summary'),
    ])
      .then(([profileRes, summaryRes]) => {
        setProfileData(profileRes.data.user);
        if (summaryRes.data.success) {
          setSummary(summaryRes.data.data);
        }
      })
      .catch(err => {
        console.error(err);
        showMessage('error', 'Error fetching dashboard data');

        if (err.response?.status === 401) {
          logout();
          navigate('/login');
        }
      })
      .finally(() => setIsLoading(false));
  }, []);

  const getNextMonday = () => {
    const today = new Date();
    const offset = (8 - today.getDay()) % 7 || 7;
    const nextMonday = new Date(today);
    nextMonday.setDate(today.getDate() + offset);
    return nextMonday.toISOString().split('T')[0];
  };

  const nextMondayDate = getNextMonday();

  const formatCurrency = (v) =>
    (v || 0).toLocaleString('en-CA', { style: 'currency', currency: 'CAD' });

  if (isLoading) {
    return <Loading />;
  }

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto space-y-6">

      {/* Welcome header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Welcome back, {profileData?.username || 'User'}!
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Last login: <DateTimeDisplay value={profileData?.lastLogin} />
          </p>
        </div>
      </div>

      {/* Quick stat cards */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <StatCard
            label="Open Orders"
            value={summary.openOrders}
            color="blue"
            onClick={() => navigate('/orders')}
          />
          <StatCard
            label="New Enquiries"
            value={summary.pendingEnquiries}
            color="amber"
            onClick={() => navigate('/enquiries')}
          />
          <StatCard
            label="Overdue Invoices"
            value={summary.overdueInvoices}
            color="red"
            onClick={() => navigate('/invoice')}
          />
          <StatCard
            label="Today's Revenue"
            value={formatCurrency(summary.todayRevenue)}
            color="green"
            isCurrency
          />
          <StatCard
            label="Month Revenue"
            value={formatCurrency(summary.monthRevenue)}
            color="emerald"
            isCurrency
          />
          <StatCard
            label="Month Profit"
            value={formatCurrency(summary.monthProfit)}
            color={summary.monthProfit >= 0 ? 'green' : 'red'}
            isCurrency
          />
        </div>
      )}

      {/* Quick actions */}
      <div className="flex flex-wrap gap-2">
        <QuickAction icon={<ShoppingCart size={16} />} label="New Order" onClick={() => navigate('/orders/new')} />
        <QuickAction icon={<Users size={16} />} label="New Customer" onClick={() => navigate('/customer/new')} />
        <QuickAction icon={<FileText size={16} />} label="New Invoice" onClick={() => navigate('/invoice/new')} />
        <QuickAction icon={<Receipt size={16} />} label="New Expense" onClick={() => navigate('/expense/new')} />
        <QuickAction icon={<DollarSign size={16} />} label="Record Payment" onClick={() => navigate('/payment/new')} />
      </div>

      {/* Main grid: InOut + Account Balances side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <h2 className="text-lg font-bold text-gray-900 mb-3">Order Status</h2>
          <InOut />
        </div>
        <div>
          <h2 className="text-lg font-bold text-gray-900 mb-3">Account Balances</h2>
          <AccountStatusCard />
        </div>
      </div>

      {/* Financial Overview */}
      <div>
        <h2 className="text-lg font-bold text-gray-900 mb-3">Financial Overview</h2>
        <IncomeWidget />
      </div>

      {/* Income vs Expense chart */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-4 border-b border-gray-100">
          <h2 className="text-lg font-bold text-gray-900">Income vs Expense</h2>
        </div>
        <IncomeExpenseWidget />
      </div>

      {/* Items needed */}
      <div>
        <h2 className="text-lg font-bold text-gray-900 mb-3">Items Needed by Date</h2>
        <ItemsNeededByDate defaultDate={nextMondayDate} />
      </div>
    </div>
  );
};

// ── Sub-components ──

function StatCard({ label, value, color = 'gray', onClick, isCurrency = false }) {
  const colorMap = {
    blue: 'bg-blue-50 border-blue-200 text-blue-700',
    green: 'bg-green-50 border-green-200 text-green-700',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    red: 'bg-red-50 border-red-200 text-red-700',
    amber: 'bg-amber-50 border-amber-200 text-amber-700',
    gray: 'bg-gray-50 border-gray-200 text-gray-700',
  };

  return (
    <div
      className={`rounded-lg border p-3 ${colorMap[color] || colorMap.gray} ${
        onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''
      }`}
      onClick={onClick}
    >
      <p className="text-xs font-medium uppercase tracking-wide opacity-75">{label}</p>
      <p className={`font-bold mt-1 ${isCurrency ? 'text-lg' : 'text-2xl'}`}>
        {value}
      </p>
    </div>
  );
}

function QuickAction({ icon, label, onClick }) {
  return (
    <button
      onClick={onClick}
      className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-colors shadow-sm"
    >
      {icon}
      {label}
    </button>
  );
}

export default Dashboard;
